/* global _filepicker_key, _s3_bucket */

const DEFAULT_FILE_MAX_SIZE = 20 * 1024 * 1024;

/**
 * Opens filestack picker with reasonable defaults.
 *
 * @param {Object} Picker params
 * @returns {Promise<Object>}
 */
export async function filestackPicker({
  path,
  maxSingleFileSize = null,
  maxFiles = 1,
  transformations = {},
  accept = undefined,
  maxWidth = null,
  maxHeight = null,
  onClose = undefined,
} = {}) {
  // Unset path lead to files ending up at the root of the S3 bucket, we should never allow this
  if (!path || path == "/") throw new Error("Non root path must be given for picker to work");

  const filestack = await import("filestack-js");

  return new Promise(function (resolve, reject) {
    const maxSize = (maxSingleFileSize ? maxSingleFileSize : DEFAULT_FILE_MAX_SIZE) * maxFiles;

    const options = {
      lang: window._locale,
      storeTo: {
        location: "S3",
        container: _s3_bucket,
        path,
        region: "eu-west-1",
      },
      fromSources: ["local_file_system"],
      maxSize,
      maxFiles,
      accept,
      transformations,
      onUploadDone: (res) => resolve(res),
      onFileUploadFailed: (error) => reject(error),
      onClose,
    };

    if (maxWidth || maxHeight) {
      options.imageMax = [maxWidth, maxHeight];
    }

    filestack.init(_filepicker_key).picker(options).open();
  });
}

/**
 * Uploads a file the page already holds, without opening the picker.
 *
 * @param {File} file
 * @param {{maxSize?: number}} options
 * @returns {Promise<{url: string, name: string, mime: string, size: number}>}
 */
export async function uploadFile(file, { maxSize = DEFAULT_FILE_MAX_SIZE } = {}) {
  if (file.size > maxSize) throw new Error(`File is ${file.size} bytes, over the ${maxSize} bytes limit`);

  const filestack = await import("filestack-js");

  const uploaded = await filestack.init(_filepicker_key).upload(
    file,
    {},
    {
      location: "S3",
      container: _s3_bucket,
      path: "/documents/filestack/",
      region: "eu-west-1",
    }
  );

  return { url: uploaded.url, name: uploaded.filename, mime: uploaded.mimetype, size: uploaded.size };
}

/**
 * @deprecated please use filestackPicker instead
 */
export async function pickDocument(storagePath, cb, accept) {
  const filestack = await import("filestack-js");

  filestack
    .init(_filepicker_key)
    .picker({
      lang: window._locale,
      storeTo: {
        location: "S3",
        container: _s3_bucket,
        path: storagePath,
        region: "eu-west-1",
      },
      fromSources: ["local_file_system"],
      maxSize: DEFAULT_FILE_MAX_SIZE * 25,
      maxFiles: 1,
      accept: accept,
      transformations: { crop: true, rotate: true, circle: true },
      onFileUploadFinished: (blob) => {
        cb(true, blob.url, blob.filename, blob.mimetype, blob.size, blob.handle);
      },
      onFileUploadFailed: () => cb(false),
    })
    .open();
}

/**
 * @deprecated please use filestackPicker instead
 */
export async function pickDocuments(cb) {
  const filestack = await import("filestack-js");

  filestack
    .init(_filepicker_key)
    .picker({
      lang: window._locale,
      storeTo: {
        location: "S3",
        container: _s3_bucket,
        path: "/documents/filestack/",
        region: "eu-west-1",
      },
      fromSources: ["local_file_system"],
      maxSize: DEFAULT_FILE_MAX_SIZE * 25,
      maxFiles: 20,
      transformations: { crop: true, rotate: true, circle: true },
      onUploadDone: ({ filesUploaded }) => cb(true, filesUploaded),
      onFileUploadFailed: () => cb(false),
    })
    .open();
}
